
import React, { useState, useEffect } from 'react';
import { Loader2, User, Shield, Plus } from 'lucide-react';
import { AdminService } from '../../../application/services/AdminService';
import { useToast } from '../../../context/ToastContext';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';

const CompanyUsersModal = ({ empresa }) => {
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isCreating, setIsCreating] = useState(false);
    const [newUser, setNewUser] = useState({
        nombre: '',
        email: '',
        usuario: '',
        cedula: '',
        password: '',
        roles: ['Administrador'] // Default para SuperAdmin creando users
    });
    
    const toast = useToast();

    useEffect(() => {
        if (empresa?.id) {
            fetchUsuarios();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [empresa]);

    const fetchUsuarios = async () => {
        setLoading(true);
        try {
            const data = await AdminService.getUsuariosEmpresa(empresa.id);
            setUsuarios(data);
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Error al cargar usuarios');
        } finally {
            setLoading(false);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await AdminService.createUsuarioEmpresa(empresa.id, newUser);
            toast.success('Usuario creado exitosamente');
            setIsCreating(false);
            setNewUser({ nombre: '', email: '', usuario: '', cedula: '', password: '', roles: ['Administrador'] });
            fetchUsuarios();
        } catch (error) {
            toast.error(error.toString());
        } finally {
            setLoading(false);
        }
    };

    if (loading && !usuarios.length && !isCreating) {
        return (
            <div className="flex justify-center items-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    return (
        <div>
            <div className="mb-4 bg-gray-50 p-3 rounded-md border border-gray-100 flex items-center justify-between">
                 <div>
                    <h4 className="text-sm font-medium text-gray-900">Empresa: {empresa.nombre}</h4>
                    <p className="text-xs text-gray-500">Listado de usuarios registrados en esta junta.</p>
                </div>
                {!isCreating && (
                    <Button size="sm" onClick={() => setIsCreating(true)} className="flex items-center text-xs">
                        <Plus className="w-3 h-3 mr-1" />
                        Nuevo Usuario
                    </Button>
                )}
            </div>

            {isCreating ? (
                <form onSubmit={handleCreateUser} className="bg-white p-4 border rounded-lg mb-4 shadow-sm animate-fade-in">
                    <h5 className="font-semibold text-gray-800 mb-3 text-sm">Registrar Nuevo Usuario</h5>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                         <Input 
                            label="Nombre Completo" 
                            value={newUser.nombre} 
                            onChange={e => setNewUser({...newUser, nombre: e.target.value})} 
                            required 
                            className="text-sm"
                        />
                         <Input 
                            label="Cédula" 
                            value={newUser.cedula} 
                            onChange={e => setNewUser({...newUser, cedula: e.target.value})} 
                            required 
                            className="text-sm"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-3 mb-3">
                         <Input 
                            label="Email" 
                            type="email"
                            value={newUser.email} 
                            onChange={e => setNewUser({...newUser, email: e.target.value})} 
                            required 
                            className="text-sm"
                        />
                         <Input 
                            label="Usuario (Login)" 
                            value={newUser.usuario} 
                            onChange={e => setNewUser({...newUser, usuario: e.target.value})} 
                            required 
                            className="text-sm"
                        />
                    </div>
                    <div className="mb-4">
                        <Input 
                            label="Contraseña" 
                            type="password"
                            value={newUser.password} 
                            onChange={e => setNewUser({...newUser, password: e.target.value})} 
                            required 
                            className="text-sm"
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="secondary" onClick={() => setIsCreating(false)} size="sm">Cancelar</Button>
                        <Button type="submit" size="sm" isLoading={loading}>Guardar Usuario</Button>
                    </div>
                </form>
            ) : (
                <div className="overflow-hidden border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Usuario</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rol</th>
                                <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {usuarios.length === 0 ? (
                                 <tr>
                                    <td colSpan="3" className="px-6 py-4 text-center text-sm text-gray-500">
                                        No hay usuarios registrados.
                                    </td>
                                </tr>
                            ) : (
                                usuarios.map((user) => (
                                    <tr key={user.id}>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div className="flex items-center">
                                                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600">
                                                    <User className="w-4 h-4" />
                                                </div>
                                                <div className="ml-3">
                                                    <div className="text-sm font-medium text-gray-900">{user.nombre}</div>
                                                    <div className="text-xs text-gray-500">{user.email || user.usuario}</div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 flex items-center">
                                                {user.roles && user.roles.includes('Administrador') && <Shield className="w-3 h-3 mr-1 text-yellow-500" />}
                                                {user.roles ? user.roles.join(', ') : 'Usuario'}
                                            </div>
                                        </td>
                                        <td className="px-4 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                user.activo ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                            }`}>
                                                {user.activo ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default CompanyUsersModal;
